import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ordersApi, adminApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLookups } from '../context/LookupContext';
import { fmtDate, statusClass, exportToExcel } from '../utils/helpers';
import TopNav from '../components/layout/TopNav';

export default function QueuePage({ myFilesOnly = false }) {
  const { user, isAdmin } = useAuth();
  const { lookups, lookupId } = useLookups();
  const navigate = useNavigate();
  const location = useLocation();

  // Restore filters from navigation state (back-button preservation)
  const savedState = location.state?.queueState || {};

  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus,   setFilterStatus]   = useState(savedState.filterStatus   ?? 'Open');
  const [filterAssigned, setFilterAssigned] = useState(savedState.filterAssigned ?? '');
  const [filterFileType, setFilterFileType] = useState(savedState.filterFileType ?? '');
  const [closingFrom, setClosingFrom] = useState(savedState.closingFrom ?? '');
  const [closingTo, setClosingTo] = useState(savedState.closingTo ?? '');
  const [allOrders, setAllOrders] = useState([]);
  const [sortCol, setSortCol] = useState(savedState.sortCol ?? null);
  const [sortDir, setSortDir] = useState(savedState.sortDir ?? 'asc');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) {
        const sid = lookupId('orderStatuses', filterStatus);
        if (sid) params.statusId = sid;
      }
      if (filterFileType) {
        const fid = lookupId('fileTypes', filterFileType);
        if (fid) params.fileTypeId = fid;
      }
      if (filterAssigned) params.assignedToId = filterAssigned;
      if (!myFilesOnly && closingFrom) params.closingFrom = closingFrom;
      if (!myFilesOnly && closingTo) params.closingTo = closingTo;
      if (myFilesOnly) params.assignedToMe = true;

      const [data, all] = await Promise.all([
        ordersApi.getAll(params),
        ordersApi.getAll({}),
      ]);
      setOrders(data);
      setAllOrders(all);
    } catch (err) {
      console.error('Failed to load orders', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterAssigned, filterFileType, closingFrom, closingTo, myFilesOnly, lookups]);

  const metrics = useMemo(() => ({
    open: allOrders.filter(o => o.statusName === 'Open').length,
    currentView: orders.length,
    mine: allOrders.filter(o => o.statusName === 'Open' && o.assignedToId === user?.userId).length,
  }), [allOrders, orders, user]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    if (isAdmin) adminApi.getUsers().then(setUsers).catch(console.error);
  }, [isAdmin]);

  // Build queue state snapshot for passing to order detail (back-button restore)
  const queueState = { filterStatus, filterAssigned, filterFileType, closingFrom, closingTo, sortCol, sortDir };

  function handleRowClick(orderId) {
    navigate(`/orders/${orderId}`, { state: { from: location.pathname, queueState } });
  }

  // Column sort
  function handleSort(col) {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  function SortIcon({ col }) {
    if (sortCol !== col) return <span style={{ color: '#ccc', marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  const sortedOrders = useMemo(() => {
    if (!sortCol) return orders;
    return [...orders].sort((a, b) => {
      let va = a[sortCol] ?? '';
      let vb = b[sortCol] ?? '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [orders, sortCol, sortDir]);

  async function handleExport() {
    const headers = ['File #', 'File Type', 'Date Ordered', 'Borrower Last Name', 'Loan Number',
      'State', 'Closing Date', 'Status'];
    if (!myFilesOnly) headers.push('Assigned To');
    headers.push('Lender', 'Broker');
    if (myFilesOnly) headers.push('Client Contact');
    headers.push('Last Note');

    const rows = [headers];
    sortedOrders.forEach(o => {
      const row = [
        `#${o.fileNumber}`, o.fileTypeName || '', fmtDate(o.dateOrdered),
        o.borrowerLastName || '', o.loanNumber || '', o.state || '',
        fmtDate(o.closingDate), o.statusName || '',
      ];
      if (!myFilesOnly) row.push(o.assignedToName || '');
      row.push(o.lender || '', o.broker || '');
      if (myFilesOnly) row.push(o.clientContact || '');
      row.push(fmtDate(o.lastNoteDate));
      rows.push(row);
    });
    await exportToExcel(rows, 'Queue', 'Queue');
  }

  const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  return (
    <div>
      <TopNav />
      <div className="page-content">
        <div className="metric-row">
          <MetricCard label="Open files"    value={metrics.open} />
          <MetricCard label="Current View"  value={metrics.currentView} />
          <MetricCard label="My open files" value={metrics.mine} />
        </div>

        <div className="card">
          <div className="d-flex align-center justify-between mb-2" style={{ flexWrap: 'wrap', gap: 10 }}>
            <h3>{myFilesOnly ? 'My files' : 'All files'}</h3>
            <div className="d-flex align-center gap-8" style={{ flexWrap: 'wrap' }}>
              {/* Status filter */}
              <FilterInline label="Status">
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="Open">Open</option>
                  <option value="">All</option>
                  <option value="Closed">Closed</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Hold">Hold</option>
                </select>
              </FilterInline>
              {/* File Type filter */}
              <FilterInline label="File type">
                <select value={filterFileType} onChange={e => setFilterFileType(e.target.value)}>
                  <option value="">All</option>
                  {lookups.fileTypes.map(t => <option key={t.id}>{t.name}</option>)}
                </select>
              </FilterInline>
              {/* Assigned filter — Admin only, not shown on My Files */}
              {isAdmin && !myFilesOnly && (
                <FilterInline label="Assigned">
                  <select value={filterAssigned} onChange={e => setFilterAssigned(e.target.value)}>
                    <option value="">All</option>
                    {users.filter(u => u.isActive).map(u => (
                      <option key={u.id} value={u.id}>{u.fullName}</option>
                    ))}
                  </select>
                </FilterInline>
              )}
              {!myFilesOnly && (
                <FilterInline label="Closing">
                  <div className="d-flex align-center gap-8">
                    <input
                      type="date"
                      value={closingFrom}
                      onChange={e => setClosingFrom(e.target.value)}
                      aria-label="Closing date start"
                      style={{ width: 125 }}
                    />
                    <span style={{ color: '#888', fontSize: 12 }}>to</span>
                    <input
                      type="date"
                      value={closingTo}
                      onChange={e => setClosingTo(e.target.value)}
                      aria-label="Closing date end"
                      style={{ width: 125 }}
                    />
                  </div>
                </FilterInline>
              )}
              <button className="btn btn-sm" onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <DownloadIcon /> Export to Excel
              </button>
            </div>
          </div>

          <div className="queue-wrap">
            <table className="queue-table compact-queue-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('fileNumber')} style={thStyle}>File # <SortIcon col="fileNumber" /></th>
                  <th onClick={() => handleSort('fileTypeName')} style={thStyle}>Type <SortIcon col="fileTypeName" /></th>
                  <th onClick={() => handleSort('dateOrdered')} style={thStyle}>Ordered <SortIcon col="dateOrdered" /></th>
                  <th onClick={() => handleSort('borrowerLastName')} style={thStyle}>Borrower <SortIcon col="borrowerLastName" /></th>
                  <th onClick={() => handleSort('loanNumber')} style={thStyle}>Loan number <SortIcon col="loanNumber" /></th>
                  <th onClick={() => handleSort('state')} style={thStyle}>State <SortIcon col="state" /></th>
                  <th onClick={() => handleSort('closingDate')} style={thStyle}>Closing <SortIcon col="closingDate" /></th>
                  <th onClick={() => handleSort('statusName')} style={thStyle}>Status <SortIcon col="statusName" /></th>
                  {!myFilesOnly && (
                    <th onClick={() => handleSort('assignedToName')} style={thStyle}>Assigned <SortIcon col="assignedToName" /></th>
                  )}
                  <th onClick={() => handleSort('lender')} style={thStyle}>Lender <SortIcon col="lender" /></th>
                  <th onClick={() => handleSort('broker')} style={thStyle}>Broker <SortIcon col="broker" /></th>
                  {myFilesOnly && (
                    <th onClick={() => handleSort('clientContact')} style={thStyle}>Contact <SortIcon col="clientContact" /></th>
                  )}
                  <th onClick={() => handleSort('lastNoteDate')} style={thStyle}>Last Note <SortIcon col="lastNoteDate" /></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading...</td></tr>
                ) : sortedOrders.length === 0 ? (
                  <tr><td colSpan={12} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>No files match the selected filters.</td></tr>
                ) : sortedOrders.map(o => (
                  <tr key={o.id} onClick={() => handleRowClick(o.id)}>
                    <td><strong>#{o.fileNumber}</strong></td>
                    <td>{o.fileTypeName || '—'}</td>
                    <td>{fmtDate(o.dateOrdered)}</td>
                    <td><strong>{o.borrowerLastName || '—'}</strong></td>
                    <td>{o.loanNumber || '—'}</td>
                    <td>{o.state || '—'}</td>
                    <td>{fmtDate(o.closingDate)}</td>
                    <td>{o.statusName ? <span className={`badge ${statusClass(o.statusName)}`}>{o.statusName}</span> : '—'}</td>
                    {!myFilesOnly && <td style={{ color: '#888' }}>{o.assignedToName || '—'}</td>}
                    <td className="queue-cell-truncate" title={o.lender || ''}><span>{o.lender || '—'}</span></td>
                    <td className="queue-cell-truncate" title={o.broker || ''}><span>{o.broker || '—'}</span></td>
                    {myFilesOnly && (
                      <td className="queue-cell-truncate" title={o.clientContact || ''}><span>{o.clientContact || '—'}</span></td>
                    )}
                    <td>{fmtDate(o.lastNoteDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterInline({ label, children }) {
  return (
    <div className="d-flex align-center gap-8">
      <label style={{ margin: 0, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</label>
      {React.cloneElement(children, { style: { ...children.props.style, width: 'auto', fontSize: 12, padding: '5px 10px' } })}
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-val">{value}</div>
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

import React from 'react';
